/*
 * Copyright (c) 2017, Indigen Solutions. All rights reserved.
 *
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 *
 * This software is the confidential and proprietary information.
 *
 * You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement
 * you entered into.
 */

import { map, values, isEmpty, chain } from "lodash";
import { Resource } from "./Resource";
import { Error } from "./Error";
import { Model } from "./Model";
import { RawDocument } from "./RawDocument";
import { ResourceIdentifier } from "./ResourceIdentifier";

/**
 *
 */
export abstract class Document<T extends Model> {

    private errors: Error[] = null;
    private meta: { [name: string]: any } = null;
    private included: { [key: string]: Resource<Model> } = {};

    /**
     *
     */
    setErrors(errors: Error[]): Document<T> {
        this.errors = errors;

        return this;
    }

    /**
     *
     */
    addError(error: Error): Document<T> {
        if (this.errors === null) {
            this.errors = [];
        }

        this.errors.push(error);

        return this;
    }

    /**
     *
     */
    hasError(): boolean {
        return (this.errors !== null) && (this.errors.length > 0);
    }

    /**
     *
     */
    getErrors(): Error[] {
        return this.errors;
    }

    /**
     *
     */
    setMeta(meta: { [name: string]: any }): Document<T> {
        this.meta = meta;

        return this;
    }

    /**
     *
     */
    getMeta(): { [name: string]: any } {
        return this.meta;
    }

    /**
     *
     */
    include(resource: Resource<Model>): Document<T> {
        this.included[`${resource.getType()}:${resource.getId()}`] = resource;
        return this;
    }

    /**
     *
     */
    getAllIncluded(): Resource<Model>[] {
        return values(this.included);
    }

    /**
     *
     */
    getIncluded<U extends Model>(identifier: ResourceIdentifier): Resource<U> {
        return this.included[this._includedKey(identifier.type, identifier.id)] as Resource<U>;
    }

    /**
     *
     */
    asArrayDocument(): ArrayDocument<T> {
        if (this instanceof ArrayDocument) {
            return this as ArrayDocument<T>;
        } else if (this instanceof SingleDocument) {
            let data: Resource<T> = (this as SingleDocument<T>).getResource();
            return new ArrayDocument<T>([data]);
        }
        throw new Error("unexpected document mode");
    }

    /**
     *
     */
    asSingleDocument(index: number = 0): SingleDocument<T> {
        if (this instanceof SingleDocument) {
            return this as SingleDocument<T>;
        } else if (this instanceof ArrayDocument) {
            if ((this as ArrayDocument<T>).length() > index) {
                return new SingleDocument<T>((this as ArrayDocument<T>).getResource(index));
            }
            throw new Error(`Invalid index ${index}`);
        }
        throw new Error("unexpected document mode");
    }

    /**
     *
     */
    toRaw(): RawDocument {
        const raw: RawDocument = {};

        if (this.meta !== null) {
            raw.meta = this.meta;
        }

        if (this.errors !== null) {
            raw.errors = this.errors;
        }

        if (!isEmpty(this.included)) {
            raw.included = map(this.included, resource => resource.toRaw());
        }

        return raw;
    }

    // TODO: @JC to remove, use resourceidentifier instead.
    private _includedKey(type: string, id: string): string {
        return `${type}:${id}`;
    }
}

/**
 *
 */
export class ArrayDocument<T extends Model> extends Document<T> {

    /**
     *
     */
    constructor(private data: Resource<T>[] = null) {
        super();
    }

    /**
     *
     */
    addResource(resource: Resource<T>): ArrayDocument<T> {
        if (this.data === null) {
            this.data = [];
        }
        if (resource) {
            this.data.push(resource);
        }
        return this;
    }

    /**
     *
     */
    getResources(): Resource<T>[] {
        return this.data;
    }

    /**
     *
     */
    getResource(index: number): Resource<T> {
        return this.data !== null ? this.data[index] : undefined;
    }

    /**
     *
     */
    getModels(): T[] {
        return chain(this.getResources())
            .compact()
            .map(resource => resource.getModel())
            .value()
            ;
    }

    /**
     *
     */
    length(): number {
        return this.data !== null ? this.data.length : 0;
    }

    /**
     *
     */
    toRaw(): RawDocument {
        const raw = super.toRaw();

        if (this.data !== null) {
            raw.data = map(this.data, resource => resource.toRaw());
        }

        return raw;
    }
}

/**
 *
 */
export class SingleDocument<T extends Model> extends Document<T> {

    /**
     *
     */
    constructor(private data: Resource<T> = null) {
        super();
    }

    /**
     *
     */
    setResource(resource: Resource<T>): SingleDocument<T> {
        this.data = resource;

        return this;
    }

    /**
     *
     */
    getResource(): Resource<T> {
        return this.data;
    }

    /**
     *
     */
    getModel(): T {
        return this.getResource().getModel();
    }

    /**
     *
     */
    toRaw(): RawDocument {
        const raw = super.toRaw();

        if (this.data !== null) {
            raw.data = this.getResource().toRaw();
        }

        return raw;
    }
}



// WEBPACK FOOTER //
// ./src/utils/json-api/Document.ts